const ENTER_MS = 420;
const LEAVE_MS = 340;

type TimelineElements = {
  backdrop: HTMLElement | null;
  content: HTMLElement | null;
};

const elementsOf = (root: Element): TimelineElements => ({
  backdrop: root.querySelector("[data-fan-backdrop]"),
  content: root.querySelector("[data-fan-content]"),
});

const sourceTransform = (source: DOMRect) => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const scale = Math.max(0.05, source.width / width);
  const x = source.left + source.width / 2 - width / 2;
  const y = source.top + source.height / 2 - height / 2;
  return `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
};

const play = (
  root: Element,
  source: DOMRect | null,
  direction: "enter" | "leave",
  reducedMotion: boolean,
  done: () => void,
) => {
  const { backdrop, content } = elementsOf(root);
  if (!backdrop || !content || reducedMotion || !source) {
    done();
    return;
  }

  const entering = direction === "enter";
  const duration = entering ? ENTER_MS : LEAVE_MS;
  const backdropAnimation = backdrop.animate(
    entering
      ? [{ opacity: 0 }, { opacity: 1 }]
      : [{ opacity: 1 }, { opacity: 0 }],
    {
      duration,
      easing: entering ? "ease-out" : "ease-in",
      fill: "both",
    },
  );
  const contentAnimation = content.animate(
    entering
      ? [
          { transform: sourceTransform(source), opacity: 0.35 },
          { transform: "translate3d(0, 0, 0) scale(1)", opacity: 1 },
        ]
      : [
          { transform: "translate3d(0, 0, 0) scale(1)", opacity: 1 },
          { transform: sourceTransform(source), opacity: 0.18 },
        ],
    {
      duration,
      easing: entering
        ? "cubic-bezier(0.32, 0.72, 0, 1)"
        : "cubic-bezier(0.4, 0, 0.2, 1)",
      fill: "both",
    },
  );

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    done();
  };
  Promise.allSettled([
    backdropAnimation.finished,
    contentAnimation.finished,
  ]).then(finish);
  window.setTimeout(finish, duration + 100);
};

export const createFanTimeline = (
  source: () => DOMRect | null,
  reducedMotion: boolean,
) => ({
  enter: (root: Element, done: () => void) =>
    play(root, source(), "enter", reducedMotion, done),
  leave: (root: Element, done: () => void) =>
    play(root, source(), "leave", reducedMotion, done),
});
